import { hasPermission, type Permission } from '@i9portal/shared';
import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { ForbiddenState } from '../../components/forbidden-state';
import { PageSpinner } from '../../components/spinner';
import { useAuth } from './use-auth';

/** Exige usuário autenticado; caso contrário redireciona para /login (guardando a rota de origem). */
export function RequireAuth({ children }: { children?: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <PageSpinner label="Verificando sessão…" />;
  if (status === 'anonymous') {
    const from = `${location.pathname}${location.search}`;
    return <Navigate to="/login" replace state={{ from }} />;
  }
  return <>{children ?? <Outlet />}</>;
}

interface RequirePermissionProps {
  /** Permissão (ou lista) exigida. */
  permission: Permission | Permission[];
  /** `any`: basta uma; `all` (padrão): todas. */
  mode?: 'any' | 'all';
  children?: ReactNode;
  /** Conteúdo exibido quando não há permissão (padrão: "Acesso negado"). */
  fallback?: ReactNode;
}

/** Protege rotas/trechos da interface por permissão (a API também valida). */
export function RequirePermission({
  permission,
  mode = 'all',
  children,
  fallback,
}: RequirePermissionProps) {
  const { me } = useAuth();
  const required = Array.isArray(permission) ? permission : [permission];
  const allowed =
    mode === 'any'
      ? required.some((p) => hasPermission(me?.permissions, p))
      : required.every((p) => hasPermission(me?.permissions, p));
  if (!allowed) return <>{fallback ?? <ForbiddenState />}</>;
  return <>{children ?? <Outlet />}</>;
}

/** Exige cadastro de funcionário (necessário para criar/consultar as próprias solicitações). */
export function RequireEmployee({ children }: { children?: ReactNode }) {
  const { me } = useAuth();
  if (!me?.employee) {
    return (
      <ForbiddenState message="Esta área é destinada a usuários com cadastro de funcionário. Procure o administrador caso precise registrar solicitações." />
    );
  }
  return <>{children ?? <Outlet />}</>;
}
